import { DisconnectOutlined } from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import {
  BBDTag,
  EditSaveButton,
  ErrorNotification,
  GraviButton,
  Horizontal,
  NothingMessage,
  NotificationMessage,
  ReadableDifference,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Divider, Form, Input, Select, Skeleton } from 'antd'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'

const { Option } = Select

export function TankMonitor({ store, canEditStores }) {
  const { data: storeMetadata } = useMetadata('store')
  const [tankMonitor, setTankMonitor] = useState()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isInError, setIsInError] = useState(false)

  const isConfigured = tankMonitor?.tank_monitor_config?.host
  const hasReadings = tankMonitor?.tank_reading_status?.last_reading_raw_text?.length > 0

  const fetchTankMonitorConfig = async () => {
    try {
      setIsLoading(true)
      setIsInError(false)
      const resp = await api.getTankMonitorConfig(store?.store_number, true)

      if (!resp?.data) {
        NotificationMessage('Tank Monitor Error', 'Failed to fetch tank monitor readings')
      }

      setTankMonitor(resp.data)
      return data
    } catch {
      setIsInError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTankMonitorConfig().then((data) => setIsEditing(!data?.tank_monitor_config?.host))
  }, [])

  const updateTankMonitorConfig = async (formData) => {
    try {
      setIsLoading(true)
      setIsInError(false)
      await api.updateTankMonitor({ store_number: store.store_number, ...formData })
    } catch {
      ErrorNotification({
        errorFields: [
          {
            errors: [`Failed to save.`],
          },
        ],
      })
      setIsInError(true)
      setIsLoading(false)
    } finally {
      setTankMonitor((tankMonitor) => ({ ...tankMonitor, tank_monitor_config: formData }))
      setIsLoading(false)
      setIsEditing(false)
    }
  }

  const onFinish = (formData) => {
    updateTankMonitorConfig(formData)
    setIsEditing(false)
  }

  return (
    <Horizontal flex='1'>
      {isLoading ? (
        <Vertical>
          <Skeleton style={{ padding: '1rem' }} />
        </Vertical>
      ) : (
        <Vertical className='p-3' flex='2'>
          <EditHeader isEditing={isEditing} setIsEditing={setIsEditing} canEditStores={canEditStores} />
          {tankMonitor?.tank_reading_status?.as_of && !isEditing && (
            <LastReadingStatus tankReadings={tankMonitor?.tank_reading_status} />
          )}
          <Vertical>
            {canEditStores && isEditing ? (
              <TankMonitorConfigForm
                tankMonitorOptions={storeMetadata?.monitor_types}
                setIsEditing={setIsEditing}
                tankMonitorConfig={tankMonitor?.tank_monitor_config}
                onFinish={onFinish}
              />
            ) : (
              <TankMonitorConfigDisplay tankMonitorConfig={tankMonitor?.tank_monitor_config} />
            )}
          </Vertical>
        </Vertical>
      )}
      <Vertical flex='3' height='auto' style={{ backgroundColor: '#f8f9fa' }}>
        <Horizontal className='p-3'>
          <Vertical justifyContent='center'>
            <Texto category='h4' appearance={tankMonitor?.tank_monitor_config ? 'default' : 'medium'}>
              Monitor Readings
            </Texto>
          </Vertical>
          <Vertical alignItems='end'>
            <GraviButton
              loading={isLoading}
              disabled={!isConfigured}
              buttonText='Refresh Readings'
              onClick={fetchTankMonitorConfig}
              theme2
              style={{ width: 120 }}
            />
          </Vertical>
        </Horizontal>
        <MonitorPane tankMonitor={tankMonitor} isConfigured={isConfigured} hasReadings={hasReadings} />
      </Vertical>
    </Horizontal>
  )
}

function MonitorPane({ tankMonitor, isConfigured, hasReadings }) {
  if (!isConfigured) {
    return null
  }

  if (isConfigured && !hasReadings) {
    return (
      <NothingMessage
        title='No Readings Yet'
        description='We have yet to receive a reading for this monitor, but also haven’t gotten an error. Check back soon for an update.'
      />
    )
  }
  return (
    <Vertical
      height='auto'
      verticalCenter
      className='p-3'
      scroll
      style={{ background: 'var(--primary-gradient)', maxHeight: '250px', overflow: 'scroll' }}
    >
      <Texto appearance='white' style={{ fontFamily: 'droid-sans-mono, monospace', whiteSpace: 'pre' }}>
        {tankMonitor?.tank_reading_status?.last_reading_raw_text}
      </Texto>
    </Vertical>
  )
}

function EditHeader({ isEditing, setIsEditing, canEditStores }) {
  return (
    <Horizontal className='pr-3'>
      <Vertical flex='1' className='pb-4'>
        <Texto category='h4'>Tank Monitor Configuration</Texto>
      </Vertical>
      <Vertical style={{ maxWidth: 27 }}>
        {!isEditing && canEditStores && <EditSaveButton editing={isEditing} onEdit={() => setIsEditing(true)} />}
      </Vertical>
    </Horizontal>
  )
}
function EmptyMessage() {
  return (
    <Vertical>
      <Horizontal>
        <DisconnectOutlined className='mr-1' style={{ fontSize: '28px', color: '#dee2e6' }} />
        <Vertical>
          <Texto category='h5'>No Monitor Configured</Texto>
          <Texto>Add a configuration to begin integrating readings.</Texto>
        </Vertical>
      </Horizontal>
      <Divider style={{ margin: '4px 0' }} />
    </Vertical>
  )
}

function LastReadingStatus({ tankReadings }) {
  const isSuccessful = tankReadings.status === 'success'
  return (
    <Vertical>
      <Horizontal>
        <Vertical>
          <Texto appearance='medium'>Last Reading:</Texto>
          <Texto weight='bold'>{formatters.SHORT_DATE_TIME(moment(tankReadings.last_reading_date))}</Texto>
          <Texto category='label' appearance='medium'>
            &#40;{ReadableDifference(tankReadings.last_reading_date)} ago&#41;
          </Texto>
        </Vertical>
        <Vertical>
          <Texto appearance='medium'>Status:</Texto>
          <BBDTag success={isSuccessful} error={!isSuccessful}>
            {tankReadings.status}
          </BBDTag>
        </Vertical>
      </Horizontal>
      <Divider style={{ margin: '4px 0' }} />
    </Vertical>
  )
}

function TankMonitorConfigForm({ tankMonitorOptions, setIsEditing, tankMonitorConfig, onFinish }) {
  const [form] = Form.useForm()
  const wrapperRef = useRef(null)

  useEffect(() => {
    form.setFieldsValue({ ...tankMonitorConfig })
  }, [tankMonitorConfig])

  return (
    <Form form={form} onFinishFailed={ErrorNotification} onFinish={onFinish}>
      <Vertical>
        <Horizontal>
          <Vertical flex='5' className='mr-4'>
            <Texto appearance='medium'>Host</Texto>
            <Form.Item name='host' rules={[{ required: true, message: 'Please enter Monitor Host' }]}>
              <Input placeholder='Enter Monitor Host' />
            </Form.Item>
          </Vertical>
          <Vertical flex='2'>
            <Texto appearance='medium'>Port</Texto>
            <Form.Item name='port' rules={[{ required: true, message: 'Please enter Port #' }]}>
              <Input placeholder='Port #' />
            </Form.Item>
          </Vertical>
        </Horizontal>
        <Vertical>
          <Texto appearance='medium'>Monitor Type</Texto>
          <div ref={wrapperRef}>
            <Form.Item name='monitor_type' rules={[{ required: true, message: 'Please enter Monitor Type' }]}>
              <Select placeholder='Select Monitor Type' getPopupContainer={(_) => wrapperRef?.current}>
                {tankMonitorOptions?.map((item, key) => {
                  return (
                    <Option value={item} key={key}>
                      {item}
                    </Option>
                  )
                })}
              </Select>
            </Form.Item>
          </div>
        </Vertical>
      </Vertical>
      <Horizontal>
        <Form.Item>
          <GraviButton className='m-2' buttonText='Save' htmlType='submit' theme2 style={{ width: '120px' }} />
        </Form.Item>
        {tankMonitorConfig && (
          <Button className='m-2' type='cancel' onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        )}
      </Horizontal>
    </Form>
  )
}

function TankMonitorConfigDisplay({ tankMonitorConfig }) {
  return (
    <Vertical>
      <Horizontal flex='1'>
        <Vertical verticalCenter>
          <Texto appearance='medium'>Host</Texto>
          <Texto category='h5'>{tankMonitorConfig?.host}</Texto>
        </Vertical>
        <Vertical verticalCenter>
          <Texto appearance='medium'>Port</Texto>
          <Texto category='h5'>{tankMonitorConfig?.port}</Texto>
        </Vertical>
      </Horizontal>
      <Vertical flex='1'>
        <Texto appearance='medium'>Monitor Type</Texto>
        <Texto category='h4'>{tankMonitorConfig?.monitor_type}</Texto>
      </Vertical>
    </Vertical>
  )
}
